/**
 * Billing Tab — Account Detail
 *
 * Shows subscription plan, payment method, and paginated invoice history
 * for a specific tenant. Loads real data from the billing store.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@respectly/ui';
import { useAuth } from '@respectly/utils/auth';
import {
  AlertTriangle,
  Building2,
  Calendar,
  CalendarRange,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Receipt,
  RefreshCw,
  RotateCcw,
  Scale,
  XCircle,
  Zap,
} from 'lucide-react';
import type { AccountDetail, InvoiceStatus } from '@respectly/shared-types';
import { BillingStatusBadge } from '../AccountStatusBadge';
import { useBillingStore } from '../../../stores/useBillingStore';
import { generateInvoicePdf } from '../../../lib/pdfGeneration';

// ============================================
// Props
// ============================================

interface BillingTabProps {
  account: AccountDetail;
}

// ============================================
// Formatting helpers
// ============================================

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(date: Date | string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
}

function formatPeriod(start: Date | string, end: Date | string) {
  const fmt = (d: Date | string) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(d));
  return `${fmt(start)} – ${fmt(end)}, ${new Date(end).getFullYear()}`;
}

// ============================================
// Invoice status config
// ============================================

const invoiceStatusConfig: Record<
  InvoiceStatus,
  { label: string; className: string; icon: ReactNode }
> = {
  paid: {
    label: 'Paid',
    className:
      'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800',
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  pending: {
    label: 'Pending',
    className:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800',
    icon: <Clock className="h-3 w-3" />,
  },
  overdue: {
    label: 'Overdue',
    className:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800',
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  draft: {
    label: 'Draft',
    className:
      'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
    icon: <FileText className="h-3 w-3" />,
  },
  cancelled: {
    label: 'Cancelled',
    className:
      'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700',
    icon: <XCircle className="h-3 w-3" />,
  },
  refunded: {
    label: 'Refunded',
    className:
      'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800',
    icon: <RotateCcw className="h-3 w-3" />,
  },
  disputed: {
    label: 'Disputed',
    className:
      'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-400 dark:border-orange-800',
    icon: <Scale className="h-3 w-3" />,
  },
};

// ============================================
// Component
// ============================================

export function BillingTab({ account }: BillingTabProps) {
  const {
    invoices,
    invoicesPagination,
    invoiceFilters,
    isLoadingInvoices,
    invoicesError,
    setInvoiceFilter,
    clearInvoiceFilters,
    loadInvoices,
  } = useBillingStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { getAccessToken } = useAuth();
  const { toast } = useToast();

  // Mount: scope invoices to this account; unmount: clear filter to avoid polluting global billing view
  useEffect(() => {
    setInvoiceFilter('tenantId', account.id);
    // setInvoiceFilter auto-calls loadInvoices(1)
    return () => {
      clearInvoiceFilters();
    };
  }, [account.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusFilter = (value: string) => {
    setCurrentPage(1);
    setInvoiceFilter('status', value === 'all' ? undefined : (value as InvoiceStatus));
  };

  const handleDateFilter = (value: string) => {
    setCurrentPage(1);
    setInvoiceFilter('dateRange', value as 'all' | '7days' | '30days' | '90days');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadInvoices(page);
  };

  const totalPages = invoicesPagination?.totalPages ?? 1;
  const selectedInvoice = selectedInvoiceId
    ? invoices.find((invoice) => invoice.id === selectedInvoiceId) ?? null
    : null;
  const hasActiveFilters = !!(
    invoiceFilters.status ||
    (invoiceFilters.dateRange && invoiceFilters.dateRange !== 'all')
  );

  const handleDownload = async () => {
    if (!selectedInvoice || isDownloading) return;
    setIsDownloading(true);
    try {
      await generateInvoicePdf(selectedInvoice.id, getAccessToken);
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err instanceof Error ? err.message : 'Could not download invoice',
        variant: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Past-due banner ─────────────────────────────── */}
      {account.billingStatus === 'past_due' && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3.5 dark:border-amber-800/60 dark:bg-amber-950/20">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Outstanding balance on this account
            </p>
            <p className="mt-0.5 text-xs text-amber-700/80 dark:text-amber-400/80">
              This account has overdue invoices that may require follow-up.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 h-7 border-amber-300 bg-white/70 text-amber-800 text-xs hover:bg-amber-100 dark:border-amber-700 dark:bg-transparent dark:text-amber-300"
            onClick={() => handleStatusFilter('overdue')}
          >
            View overdue
          </Button>
        </div>
      )}

      {/* ── Summary cards ───────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-3">
        {/* Plan */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/50">
                <Zap className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  Current Plan
                </p>
                <p className="mt-0.5 truncate text-base font-semibold text-slate-900 dark:text-white">
                  {account.plan.name}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing Status */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                <Receipt className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  Billing Status
                </p>
                <div className="mt-1.5">
                  <BillingStatusBadge status={account.billingStatus} size="sm" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Provider */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  account.helcimCustomerId
                    ? 'bg-sky-50 dark:bg-sky-950/40'
                    : 'bg-slate-100 dark:bg-slate-800'
                }`}
              >
                <Building2
                  className={`h-4 w-4 ${
                    account.helcimCustomerId
                      ? 'text-sky-600 dark:text-sky-400'
                      : 'text-slate-400 dark:text-slate-600'
                  }`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  Payment Provider
                </p>
                {account.helcimCustomerId ? (
                  <p className="mt-0.5 truncate text-base font-semibold text-slate-900 dark:text-white">
                    Helcim
                  </p>
                ) : (
                  <p className="mt-0.5 text-sm text-slate-400 dark:text-slate-600">Not connected</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Payment method ──────────────────────────────── */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between pb-3 pt-4 px-5">
          <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            Payment Method
          </CardTitle>
          {account.helcimCustomerId && (
            <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
              <ExternalLink className="h-3 w-3" />
              Open in Helcim
            </Button>
          )}
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-0">
          {account.helcimCustomerId ? (
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3.5 dark:border-slate-700 dark:bg-slate-800/40">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-700 dark:ring-slate-600">
                <CreditCard className="h-4 w-4 text-slate-600 dark:text-slate-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white">Helcim</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Customer ID:{' '}
                  <span className="font-mono text-slate-700 dark:text-slate-300">
                    {account.helcimCustomerId}
                  </span>
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:ring-emerald-800">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                Connected
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2.5 py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                <CreditCard className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  No payment method on file
                </p>
                <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-600">
                  Helcim customer account not connected
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Invoice history ─────────────────────────────── */}
      <Card className="border-slate-200 dark:border-slate-800">
        {/* Toolbar */}
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 px-5 pb-3 pt-4">
          <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            Invoice History
            {invoicesPagination && (
              <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-600">
                ({invoicesPagination.total} total)
              </span>
            )}
          </CardTitle>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={invoiceFilters.status ?? 'all'} onValueChange={handleStatusFilter}>
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="disputed">Disputed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={invoiceFilters.dateRange ?? 'all'}
              onValueChange={handleDateFilter}
            >
              <SelectTrigger className="h-8 w-[120px] text-xs">
                <SelectValue placeholder="All time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="30days">Last 30 days</SelectItem>
                <SelectItem value="90days">Last 90 days</SelectItem>
                <SelectItem value="7days">Last 7 days</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 shrink-0"
              onClick={() => loadInvoices(currentPage)}
              disabled={isLoadingInvoices}
              title="Refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoadingInvoices ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Error */}
          {invoicesError && !isLoadingInvoices && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/40">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">{invoicesError.message}</p>
              <Button variant="outline" size="sm" onClick={() => loadInvoices(currentPage)}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Retry
              </Button>
            </div>
          )}

          {/* Loading skeleton */}
          {isLoadingInvoices && (
            <div className="divide-y divide-slate-50 dark:divide-slate-800/60">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <div className="ml-auto flex items-center gap-6">
                    <Skeleton className="h-3.5 w-14" />
                    <Skeleton className="h-3.5 w-10" />
                    <Skeleton className="h-3.5 w-14" />
                    <Skeleton className="h-3.5 w-20" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table */}
          {!isLoadingInvoices && !invoicesError && (
            <>
              {invoices.length === 0 ? (
                <div className="flex flex-col items-center gap-2.5 py-14 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                    <FileText className="h-5 w-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      No invoices found
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-600">
                      {hasActiveFilters
                        ? 'Try adjusting your filters'
                        : 'This account has no invoice history yet'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          Invoice
                        </th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          Period
                        </th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          Status
                        </th>
                        <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          Subtotal
                        </th>
                        <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          Tax
                        </th>
                        <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          Total
                        </th>
                        <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          Due Date
                        </th>
                        <th className="px-5 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          {/* actions */}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                      {invoices.map((invoice) => {
                        const sc = invoiceStatusConfig[invoice.status];
                        const isOverdue = invoice.status === 'overdue';
                        return (
                          <tr
                            key={invoice.id}
                            className={`group transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/20 ${
                              isOverdue ? 'bg-red-50/30 dark:bg-red-950/10' : ''
                            }`}
                          >
                            {/* Invoice # */}
                            <td className="px-5 py-3.5">
                              <p className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200">
                                {invoice.invoiceNumber}
                              </p>
                              <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-600">
                                {formatDate(invoice.createdAt)}
                              </p>
                            </td>

                            {/* Period */}
                            <td className="px-4 py-3.5 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {formatPeriod(invoice.periodStart, invoice.periodEnd)}
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3.5">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${sc.className}`}
                              >
                                {sc.icon}
                                {sc.label}
                              </span>
                            </td>

                            {/* Subtotal */}
                            <td className="px-4 py-3.5 text-right text-xs tabular-nums text-slate-600 dark:text-slate-400">
                              {formatCurrency(invoice.subtotal, invoice.currency)}
                            </td>

                            {/* Tax */}
                            <td className="px-4 py-3.5 text-right text-xs tabular-nums text-slate-400 dark:text-slate-600">
                              {formatCurrency(invoice.tax, invoice.currency)}
                            </td>

                            {/* Total */}
                            <td className="px-4 py-3.5 text-right text-xs tabular-nums font-semibold text-slate-900 dark:text-white">
                              {formatCurrency(invoice.total, invoice.currency)}
                            </td>

                            {/* Due Date */}
                            <td
                              className={`px-4 py-3.5 text-right text-xs tabular-nums ${
                                isOverdue
                                  ? 'font-medium text-red-600 dark:text-red-400'
                                  : 'text-slate-500 dark:text-slate-400'
                              }`}
                            >
                              {formatDate(invoice.dueDate)}
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-3.5 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs text-slate-500 opacity-0 transition-opacity group-hover:opacity-100 dark:text-slate-400"
                                onClick={() => setSelectedInvoiceId(invoice.id)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 dark:border-slate-800">
                  <p className="text-xs text-slate-400 dark:text-slate-600">
                    Page {currentPage} of {totalPages}
                    {invoicesPagination?.total != null && (
                      <> &middot; {invoicesPagination.total} invoices</>
                    )}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={currentPage <= 1 || isLoadingInvoices}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={currentPage >= totalPages || isLoadingInvoices}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={selectedInvoice !== null} onOpenChange={(open) => !open && setSelectedInvoiceId(null)}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-2xl">
          {selectedInvoice && (
            <>
              {/* Accent strip — subtle Super Admin signature */}
              <div
                aria-hidden
                className="h-1.5 w-full bg-gradient-to-r from-slate-900 via-indigo-800 to-slate-900 dark:from-indigo-950 dark:via-indigo-900 dark:to-slate-950"
              />

              {/* Header */}
              <DialogHeader className="space-y-3 px-6 pt-5 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 space-y-1">
                    <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Invoice
                    </p>
                    <div className="flex items-center gap-2.5">
                      <DialogTitle className="truncate text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                        {selectedInvoice.invoiceNumber}
                      </DialogTitle>
                      <span
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                          invoiceStatusConfig[selectedInvoice.status].className
                        }`}
                      >
                        {invoiceStatusConfig[selectedInvoice.status].icon}
                        {invoiceStatusConfig[selectedInvoice.status].label}
                      </span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="mt-1 shrink-0 gap-1.5"
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Preparing…
                      </>
                    ) : (
                      <>
                        <Download className="h-3.5 w-3.5" />
                        Download
                      </>
                    )}
                  </Button>
                </div>
              </DialogHeader>

              {/* Body */}
              <div className="space-y-5 px-6 pb-5">
                {/* Meta strip */}
                <div className="grid grid-cols-3 divide-x divide-slate-200/80 rounded-lg border border-slate-200/80 bg-slate-50/60 dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900/40">
                  <div className="px-4 py-3">
                    <div className="mb-1 flex items-center gap-1.5 text-slate-400 dark:text-slate-500">
                      <Calendar className="h-3.5 w-3.5" />
                      <span className="text-[10px] font-medium uppercase tracking-wider">Issued</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {formatDate(selectedInvoice.createdAt)}
                    </p>
                  </div>
                  <div className="px-4 py-3">
                    <div className="mb-1 flex items-center gap-1.5 text-slate-400 dark:text-slate-500">
                      <CalendarRange className="h-3.5 w-3.5" />
                      <span className="text-[10px] font-medium uppercase tracking-wider">Billing Period</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {formatPeriod(selectedInvoice.periodStart, selectedInvoice.periodEnd)}
                    </p>
                  </div>
                  <div className="px-4 py-3">
                    <div className="mb-1 flex items-center gap-1.5 text-slate-400 dark:text-slate-500">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="text-[10px] font-medium uppercase tracking-wider">Due</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {formatDate(selectedInvoice.dueDate)}
                    </p>
                  </div>
                </div>

                {/* Line items */}
                <div>
                  <table className="w-full border-t border-b border-slate-200 dark:border-slate-800">
                    <thead>
                      <tr>
                        <th className="px-3 pt-3 pb-2 text-left text-[10px] font-medium uppercase tracking-wider text-slate-500">
                          Description
                        </th>
                        <th className="px-3 pt-3 pb-2 text-right text-[10px] font-medium uppercase tracking-wider text-slate-500">
                          Qty
                        </th>
                        <th className="px-3 pt-3 pb-2 text-right text-[10px] font-medium uppercase tracking-wider text-slate-500">
                          Unit
                        </th>
                        <th className="px-3 pt-3 pb-2 text-right text-[10px] font-medium uppercase tracking-wider text-slate-500">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 ? (
                        selectedInvoice.lineItems.map((lineItem, index) => (
                          <tr
                            key={`${selectedInvoice.id}-${index}`}
                            className="border-t border-slate-100 odd:bg-slate-50/40 dark:border-slate-800/60 dark:odd:bg-slate-900/20"
                          >
                            <td className="px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200">
                              {lineItem.description}
                            </td>
                            <td className="px-3 py-2.5 text-right text-sm tabular-nums text-slate-600 dark:text-slate-400">
                              {lineItem.quantity}
                            </td>
                            <td className="px-3 py-2.5 text-right text-sm tabular-nums text-slate-600 dark:text-slate-400">
                              {formatCurrency(lineItem.unitPrice, selectedInvoice.currency)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-sm font-medium tabular-nums text-slate-900 dark:text-slate-100">
                              {formatCurrency(lineItem.total, selectedInvoice.currency)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="border-t border-slate-100 dark:border-slate-800/60">
                          <td
                            colSpan={4}
                            className="px-3 py-4 text-center text-xs italic text-slate-400 dark:text-slate-500"
                          >
                            No line items
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="ml-auto w-full max-w-xs space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Subtotal</span>
                    <span className="tabular-nums text-slate-700 dark:text-slate-300">
                      {formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Tax</span>
                    <span className="tabular-nums text-slate-700 dark:text-slate-300">
                      {formatCurrency(selectedInvoice.tax, selectedInvoice.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-200 pt-2 dark:border-slate-700">
                    <span className="text-base font-semibold text-slate-900 dark:text-slate-50">
                      Total
                    </span>
                    <span className="text-base font-semibold tabular-nums text-slate-900 dark:text-slate-50">
                      {formatCurrency(selectedInvoice.total, selectedInvoice.currency)}
                    </span>
                  </div>
                  {selectedInvoice.status === 'paid' && selectedInvoice.paidAt && (
                    <div className="flex items-center justify-end gap-1 pt-0.5 text-xs text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" />
                      <span>Paid on {formatDate(selectedInvoice.paidAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="border-t border-slate-200 bg-slate-50/60 px-6 py-3 dark:border-slate-800 dark:bg-slate-900/40">
                <Button variant="ghost" size="sm" onClick={() => setSelectedInvoiceId(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
